/**
 * 第一人稱控制器
 *
 * - PointerLock 滑鼠控制視角
 * - WASD 行走，Shift 跑步，Space 跳躍
 * - 自動跟隨地形高度（從高度圖取值）
 */

import { useRef, useEffect, useCallback } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { useGameStore } from '../core/store/gameStore'
import {
  TERRAIN_WORLD_SIZE,
  TERRAIN_HEIGHT_SCALE,
  WATER_LEVEL,
  PLAYER_HEIGHT,
} from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'
import { playerPose } from '../core/playerPose'

const MOVE_SPEED = 8
const RUN_SPEED = 16
const JUMP_VELOCITY = 8
const GRAVITY = -20
const MOUSE_SENSITIVITY = 0.002

// 按鍵狀態
const keys: Record<string, boolean> = {}

// 預分配向量（避免 useFrame 內每幀 new）
const _forward = new THREE.Vector3()
const _right = new THREE.Vector3()
const _moveDir = new THREE.Vector3()
const _yOnlyEuler = new THREE.Euler(0, 0, 0, 'YXZ')
const _upVec = new THREE.Vector3(0, 1, 0)

export function FirstPersonController() {
  const { camera, gl } = useThree()
  const setPointerLocked = useGameStore(s => s.setPointerLocked)
  const isEnteredWorld = useGameStore(s => s.isEnteredWorld)
  const setEnteredWorld = useGameStore(s => s.setEnteredWorld)
  const setEditorMode = useGameStore(s => s.setEditorMode)

  const euler = useRef(new THREE.Euler(0, 0, 0, 'YXZ'))
  const velocity = useRef(new THREE.Vector3())
  const isOnGround = useRef(true)
  const playerPos = useRef(new THREE.Vector3(0, 20, 0))
  const spawnX = 0
  const spawnZ = 20

  // 初始化位置到地形中央偏上
  useEffect(() => {
    if (!terrainBuffer.ready) return
    const y = terrainBuffer.getHeight(spawnX, spawnZ)
    playerPos.current.set(
      spawnX,
      Math.max(y, WATER_LEVEL) + PLAYER_HEIGHT + 8,
      spawnZ,
    )
    euler.current.set(-0.08, Math.PI, 0)
    camera.position.copy(playerPos.current)
    camera.quaternion.setFromEuler(euler.current)
  }, [camera])

  // 鍵盤事件
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { keys[e.code] = true }
    const onKeyUp = (e: KeyboardEvent) => { keys[e.code] = false }

    const onEscPause = (e: KeyboardEvent) => {
      if (e.code !== 'Escape') return
      setEnteredWorld(false)
      if (!useGameStore.getState().visitMode) setEditorMode('edit')
      if (document.pointerLockElement) document.exitPointerLock()
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('keydown', onEscPause)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('keydown', onEscPause)
    }
  }, [setEnteredWorld])

  // 滑鼠移動（PointerLock）
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isEnteredWorld) return
      // 僅在指標鎖定於 Canvas 時用 movement 轉視角，避免未鎖定時滑鼠劃過畫面也旋轉
      if (document.pointerLockElement !== gl.domElement) return
      euler.current.y -= e.movementX * MOUSE_SENSITIVITY
      euler.current.x -= e.movementY * MOUSE_SENSITIVITY
      euler.current.x = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, euler.current.x))
    }

    const onPointerLockChange = () => {
      setPointerLocked(document.pointerLockElement === gl.domElement)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('pointerlockchange', onPointerLockChange)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('pointerlockchange', onPointerLockChange)
    }
  }, [gl.domElement, setPointerLocked, isEnteredWorld])

  // 點擊 canvas 進入 PointerLock
  const requestLock = useCallback(() => {
    try {
      gl.domElement.requestPointerLock()
    } catch {
      // 某些瀏覽器或環境可能拒絕 Pointer Lock，
      // 但仍允許先進入遊戲狀態。
    }
  }, [gl.domElement])

  // 顯式「進入山海世界」事件（由 overlay 觸發）
  useEffect(() => {
    const onEnterWorld = () => {
      setEnteredWorld(true)
      requestLock()
    }
    window.addEventListener('shj-enter-world', onEnterWorld)
    return () => window.removeEventListener('shj-enter-world', onEnterWorld)
  }, [requestLock, setEnteredWorld])

  useEffect(() => {
    gl.domElement.addEventListener('click', requestLock)
    return () => gl.domElement.removeEventListener('click', requestLock)
  }, [gl.domElement, requestLock])

  useFrame((_, rawDelta) => {
    if (!terrainBuffer.ready || !isEnteredWorld) return
    const delta = Math.min(rawDelta, 0.1)

    _forward.set(0, 0, -1)
    _yOnlyEuler.set(0, euler.current.y, 0)
    _forward.applyEuler(_yOnlyEuler)
    _right.crossVectors(_forward, _upVec).normalize()

    const isRunning = keys['ShiftLeft'] || keys['ShiftRight']
    const speed = isRunning ? RUN_SPEED : MOVE_SPEED

    _moveDir.set(0, 0, 0)
    if (keys['KeyW'] || keys['ArrowUp']) _moveDir.add(_forward)
    if (keys['KeyS'] || keys['ArrowDown']) _moveDir.sub(_forward)
    if (keys['KeyD'] || keys['ArrowRight']) _moveDir.add(_right)
    if (keys['KeyA'] || keys['ArrowLeft']) _moveDir.sub(_right)
    if (_moveDir.lengthSq() > 0) _moveDir.normalize()

    playerPos.current.x += _moveDir.x * speed * delta
    playerPos.current.z += _moveDir.z * speed * delta

    // 跳躍
    if ((keys['Space']) && isOnGround.current) {
      velocity.current.y = JUMP_VELOCITY
      isOnGround.current = false
    }

    // 重力
    velocity.current.y += GRAVITY * delta
    playerPos.current.y += velocity.current.y * delta

    // 地形碰撞
    const terrainY = terrainBuffer.getHeight(playerPos.current.x, playerPos.current.z)
    const groundY = Math.max(terrainY, WATER_LEVEL) + PLAYER_HEIGHT

    if (playerPos.current.y < groundY) {
      playerPos.current.y = groundY
      velocity.current.y = 0
      isOnGround.current = true
    }

    playerPose.x = playerPos.current.x
    playerPose.z = playerPos.current.z
    camera.position.copy(playerPos.current)
    camera.quaternion.setFromEuler(euler.current)
  })

  return null
}
